// Server-sent events: batch incoming `sync`/`thread` and feed events (card, work, pass, routine,
// sweep, proposal, budget, notification, brief, task, reply) and invalidate only the queries they
// touch.
// EventSource reconnects on its own for network blips; when the server closes the stream
// (restart, 401) we reconnect with capped backoff and re-check the session.
import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient, type Query, type QueryClient } from "@tanstack/react-query";
import type { FeedSseEvent } from "@shared/api";
import {
  applyFeedPlan,
  FEED_EVENT_TYPES,
  invalidateCardThreads,
  invalidateFeedAfterReconnect,
  isFeedEvent,
  planFeedInvalidation,
} from "./feedEvents";
import { queryKeys, type ThreadListKey } from "./queries";

export type ServerEvent =
  | { type: "sync"; accountId: string; kind: string }
  | { type: "thread"; threadKey: string }
  | { type: "ready" }
  | FeedSseEvent;

export interface InvalidationPlan {
  accounts: boolean;
  threadKeys: string[];
  /** Accounts whose thread lists may have changed. */
  listAccountIds: string[];
}

/** Sync phases that change account state but not the mail itself. */
const SYNC_KINDS_WITHOUT_MAIL = new Set([
  "start",
  "started",
  "progress",
  "heartbeat",
  "error",
  "failed",
  "paused",
]);

export function accountIdFromThreadKey(threadKey: string): string | null {
  const i = threadKey.indexOf(":");
  return i > 0 ? threadKey.slice(0, i) : null;
}

export function planInvalidation(events: readonly ServerEvent[]): InvalidationPlan {
  let accounts = false;
  const threadKeys = new Set<string>();
  const listAccountIds = new Set<string>();
  for (const event of events) {
    if (event.type === "sync") {
      accounts = true;
      if (!SYNC_KINDS_WITHOUT_MAIL.has(event.kind)) listAccountIds.add(event.accountId);
    } else if (event.type === "thread") {
      threadKeys.add(event.threadKey);
      const accountId = accountIdFromThreadKey(event.threadKey);
      if (accountId) listAccountIds.add(accountId);
    }
  }
  return { accounts, threadKeys: [...threadKeys], listAccountIds: [...listAccountIds] };
}

/** Does a cached thread-list query show threads from any of `accountIds`? */
export function listTouches(key: readonly unknown[], accountIds: ReadonlySet<string>): boolean {
  if (key[0] !== "threads") return false;
  const params = key[1] as ThreadListKey | undefined;
  if (!params?.accountId) return accountIds.size > 0; // unified list shows every account
  return accountIds.has(params.accountId);
}

export function applyPlan(queryClient: QueryClient, plan: InvalidationPlan): void {
  if (plan.threadKeys.length || plan.listAccountIds.length)
    void queryClient.invalidateQueries({ queryKey: ["saved-mail"] });
  if (plan.accounts) void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
  for (const key of plan.threadKeys) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.threadAll(key) });
  }
  if (plan.listAccountIds.length) {
    const ids = new Set(plan.listAccountIds);
    void queryClient.invalidateQueries({
      predicate: (q: Query) => listTouches(q.queryKey, ids),
    });
    // Search results are marked stale but not refetched under the reader.
    void queryClient.invalidateQueries({ queryKey: ["search"], refetchType: "none" });
  }
}

export type LiveStatus = "connecting" | "live" | "offline";

let liveStatus: LiveStatus = "connecting";
const statusListeners = new Set<() => void>();
function setLiveStatus(next: LiveStatus) {
  if (next === liveStatus) return;
  liveStatus = next;
  for (const l of statusListeners) l();
}

export function useLiveStatus(): LiveStatus {
  return useSyncExternalStore(
    (l) => {
      statusListeners.add(l);
      return () => statusListeners.delete(l);
    },
    () => liveStatus,
    () => liveStatus,
  );
}

/** Backoff for reconnect attempt `n` (0-based): 1s, 2s, 4s … capped at 30s, plus jitter. */
export function reconnectDelay(attempt: number, random = Math.random()): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
  return Math.round(base + base * 0.2 * random);
}

const BATCH_MS = 400;

function parse(data: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(data);
    return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function useServerEvents(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") return;
    let source: EventSource | null = null;
    let attempt = 0;
    let everConnected = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let batchTimer: ReturnType<typeof setTimeout> | undefined;
    let pending: ServerEvent[] = [];
    let stopped = false;

    const flush = () => {
      batchTimer = undefined;
      const events = pending;
      pending = [];
      const plan = planInvalidation(events);
      applyPlan(queryClient, plan);
      invalidateCardThreads(queryClient, plan.threadKeys);
      // New mail can change who owes whom a reply (the card badges and "Waiting for a reply").
      if (plan.threadKeys.length) void queryClient.invalidateQueries({ queryKey: ["replies"] });
      applyFeedPlan(queryClient, planFeedInvalidation(events.filter(isFeedEvent)));
    };
    const enqueue = (event: ServerEvent) => {
      pending.push(event);
      batchTimer ??= setTimeout(flush, BATCH_MS);
    };

    const connect = () => {
      setLiveStatus("connecting");
      const es = new EventSource("/api/mail/events", { withCredentials: true });
      source = es;
      es.onopen = () => {
        attempt = 0;
        setLiveStatus("live");
      };
      es.addEventListener("ready", () => {
        setLiveStatus("live");
        // After a reconnect we may have missed events: refresh what is on screen.
        if (everConnected) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
          void queryClient.invalidateQueries({ queryKey: ["threads"] });
          void queryClient.invalidateQueries({ queryKey: ["thread"] });
          void queryClient.invalidateQueries({ queryKey: ["saved-mail"] });
          invalidateFeedAfterReconnect(queryClient);
        }
        everConnected = true;
      });
      es.addEventListener("sync", (e) => {
        const data = parse((e as MessageEvent<string>).data);
        if (typeof data?.accountId === "string") {
          enqueue({ type: "sync", accountId: data.accountId, kind: String(data.kind ?? "") });
        }
      });
      es.addEventListener("thread", (e) => {
        const data = parse((e as MessageEvent<string>).data);
        if (typeof data?.threadKey === "string")
          enqueue({ type: "thread", threadKey: data.threadKey });
      });
      for (const type of FEED_EVENT_TYPES) {
        es.addEventListener(type, (e) => {
          const data = parse((e as MessageEvent<string>).data);
          if (data) enqueue({ ...data, type } as FeedSseEvent);
        });
      }
      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          setLiveStatus("offline");
          es.close();
          if (stopped) return;
          // The stream was refused (server restart or expired session): re-check the session.
          if (attempt >= 1) void queryClient.invalidateQueries({ queryKey: queryKeys.session });
          retryTimer = setTimeout(connect, reconnectDelay(attempt++));
        } else {
          setLiveStatus("connecting");
        }
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      clearTimeout(batchTimer);
      source?.close();
      setLiveStatus("connecting");
    };
  }, [enabled, queryClient]);
}
